// Live sync between web and desktop (and between colleagues): the API announces every write on the `/live`
// socket ({ scopes }) and this module reloads exactly that data. A focus/visibility check and a reconnect reload
// catch anything missed while the window slept or the socket was down.
import { LIVE_EVENTS, LIVE_NAMESPACE, LIVE_SESSION_INVALID, type LiveChange, type LiveScope, type LiveSession } from '@rimon/contracts';
import { io, type Socket } from 'socket.io-client';
import { API_URL, tokens } from '../api/client';
import { live, loadClosing, loadCore, loadStatements, reloadDocs, reloadImports } from './compta';
import { recheckSession } from './sessionCheck';

/** Screens with their own state (the import screen) listen for this window event. */
export const LIVE_DOM_EVENT = 'rimon:live';
/** The person's language from RimoID changed (detail: 'de' | 'fr'). */
export const LOCALE_DOM_EVENT = 'rimon:locale';

let socket: Socket | null = null, offToken: (() => void) | null = null, offFocus: (() => void) | null = null;
let lastSync = Date.now(), wasDown = false, queued = new Set<LiveScope>(), timer: ReturnType<typeof setTimeout> | null = null;
/** Refused handshakes in a row: a session that refreshes fine but is still refused must not loop. */
let refused = 0;

async function reload(scopes: Set<LiveScope>) {
  lastSync = Date.now();
  const jobs: Promise<unknown>[] = [];
  if (scopes.has('documents')) jobs.push(reloadDocs());
  if (scopes.has('imports')) jobs.push(reloadImports());
  if (scopes.has('firm')) jobs.push(loadCore());
  if (scopes.has('statements')) Object.keys(live.statements).forEach((m) => jobs.push(loadStatements(m)));
  if (scopes.has('closing')) Object.keys(live.closing).forEach((m) => jobs.push(loadClosing(m)));
  await Promise.allSettled(jobs);
  window.dispatchEvent(new CustomEvent(LIVE_DOM_EVENT, { detail: [...scopes] }));
}
const ALL: LiveScope[] = ['documents', 'imports', 'firm', 'statements', 'closing', 'pointage'];
/** Coalesces bursts (several events, focus + reconnect) into one reload. */
function schedule(scopes: Iterable<LiveScope>, delay = 150) {
  for (const s of scopes) queued.add(s);
  if (timer) return;
  timer = setTimeout(() => { timer = null; const s = queued; queued = new Set(); void reload(s); }, delay);
}

export function startLiveSync(): void {
  if (socket) return;
  socket = io(new URL(API_URL).origin + LIVE_NAMESPACE, { auth: (cb) => cb({ token: tokens.access }), transports: ['websocket'] });
  socket.on(LIVE_EVENTS.CHANGED, (b: LiveChange) => schedule(b.scopes ?? ALL));
  // RimoID changed the account / ended the session: the server says so, then drops the socket (socket.io does not
  // reconnect after a server disconnect). The refresh brings the new rights (→ reconnect via tokens.onChange) or ends
  // in „Abgemeldet“.
  // Only the language changed in RimoID: the window switches at once (LOCALE_DOM_EVENT), the socket stays.
  socket.on(LIVE_EVENTS.SESSION, (b?: LiveSession) => {
    if (b?.reason === 'locale_changed' && b.locale) { window.dispatchEvent(new CustomEvent(LOCALE_DOM_EVENT, { detail: b.locale })); return; }
    void recheckSession();
  });
  socket.on('disconnect', (reason) => { wasDown = true; if (reason === 'io server disconnect') void recheckSession(); });
  // Handshake refused (token expired or outdated, e.g. a window that slept through a change): refresh, never retry blindly.
  socket.on('connect_error', (e) => { if (e.message === LIVE_SESSION_INVALID && ++refused <= 3) void recheckSession(); });
  socket.on('connect', () => { refused = 0; if (wasDown) { wasDown = false; schedule(ALL); } });
  // The handshake reads the current access token; after a refresh a dropped socket reconnects with the new one.
  offToken = tokens.onChange(() => { if (socket && !socket.connected && tokens.access) socket.connect(); });
  const wake = () => {
    if (document.visibilityState === 'hidden') return;
    if (!socket?.connected) { socket?.connect(); schedule(ALL); }
    else if (Date.now() - lastSync > 60_000) schedule(['documents', 'imports']);
  };
  window.addEventListener('focus', wake); document.addEventListener('visibilitychange', wake);
  offFocus = () => { window.removeEventListener('focus', wake); document.removeEventListener('visibilitychange', wake); };
}

export function stopLiveSync(): void {
  socket?.disconnect(); socket = null;
  offToken?.(); offToken = null; offFocus?.(); offFocus = null;
  if (timer) clearTimeout(timer); timer = null; queued.clear(); refused = 0;
}
